import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDelivery } from '../../api/queries/delivery';
import { useTruckRecordingMipEdgeEstimation } from '../../api/queries/truckRecordingMipEdgeEstimation';
import { useTruckRecordingMipMaterialClassification } from '../../api/queries/truckRecordingMipMaterialClassification';
import { useTruckRecordingMipSoilClassification } from '../../api/queries/truckRecordingMipSoilClassification';
import { useTruckRecordingMipImage } from '../../api/queries/truckRecordingMipImage';
import { TruckRecordingMip } from '../../api/queries/truckRecordingMipList';
import { WrapperCardWithTitle } from '../../layouts/WrapperCardWithTitle';
import { DeliveryNoteGuarded } from '../Delivery/DeliveryNote';
import { DetailImageQueryGuarded } from '../DetailView/DetailImageQuery';
import { FeedbackSnackbar } from '../FeedbackSnackbar';
import { AttributesColumnQueryGuarded } from '../Prediction/AttributesColumn/AttributesColumn';
import { MaterialColumnQueryGuarded } from '../Prediction/MaterialColumn/MaterialColumn';
import { PredictionColoringTooltip } from '../Prediction/PredictionColoringTooltip';
import { PredictionColumnsLayout } from '../Prediction/PredictionColumnsLayout';
import { shouldDisplayEdgeEstimation, shouldDisplaySoilClassification } from '../Prediction/utils';
import { useFeedbackSnackbar } from '../useFeedbackSnackbar';
import { DefaultErrorScreen } from '../ApiLoadingGuard';
import { BoundingBox } from '../Documentation/ImageCard';
import { useResolvedBillableMaterials } from '../BillableMaterials/useResolvedBillableMaterials';
import BillableMaterialColumn from '../BillableMaterials/predictionColumn/BillableMaterialColumn';
import { useTruckRecordingMipVolumeEstimation } from '../../api/queries/truckRecordingMipVolumeEstimation';

export interface TruckRecordingDetailsProps {
  organisationId: string;
  isUserAdmin: boolean;
  recording: TruckRecordingMip;
}

export const TruckRecordingsDetails = ({
  organisationId,
  isUserAdmin,
  recording,
}: TruckRecordingDetailsProps): ReactElement => {
  const image = useTruckRecordingMipImage({ jobsiteId: recording.jobsiteId, mipReportId: recording.id });
  const materialClassification = useTruckRecordingMipMaterialClassification({
    jobsiteId: recording.jobsiteId,
    mipReportId: recording.id,
  });

  const displaySoilClassification = useMemo(
    () => shouldDisplaySoilClassification(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const soilClassification = useTruckRecordingMipSoilClassification({
    jobsiteId: recording.jobsiteId,
    mipReportId: recording.id,
    enabled: displaySoilClassification,
  });
  const volumeEstimation = useTruckRecordingMipVolumeEstimation({
    jobsiteId: recording.jobsiteId,
    mipReportId: recording.id,
  });
  const delivery = useDelivery({ jobsiteId: recording.jobsiteId, deliveryId: recording.deliveryId });

  // Conditionally enable edge estimation based on material classification
  const displayEdgeEstimation = useMemo(
    () => shouldDisplayEdgeEstimation(materialClassification.data?.rawPredictions),
    [materialClassification.data]
  );
  const edgeEstimation = useTruckRecordingMipEdgeEstimation({
    jobsiteId: recording.jobsiteId,
    mipReportId: recording.id,
    enabled: displayEdgeEstimation,
  });

  const { resolvedMaterials } = useResolvedBillableMaterials(
    organisationId,
    recording.jobsiteId,
    materialClassification.data,
    soilClassification.data,
    edgeEstimation?.data
  );

  const feedbackSnackbarManager = useFeedbackSnackbar({
    contentType: 'recording',
    jobsiteId: recording.jobsiteId,
    contentId: recording.imageId || '',
  });

  const [selectedAttribute, setSelectedAttribute] = useState<string | null>();
  const [edgeSizesBoundingBoxes, setEdgeSizeBoundingBoxes] = useState<BoundingBox[]>([]);

  const handleAttributeSelect = (attributeId: string) => {
    setSelectedAttribute(selectedAttribute === attributeId ? null : attributeId);
  };

  useEffect(() => {
    if (!edgeEstimation.data || edgeEstimation.data.length === 0) return;

    const edgeData = edgeEstimation.data[0];
    if (selectedAttribute === 'edge-estimation') {
      setEdgeSizeBoundingBoxes(edgeData.boundingBoxes);
    } else if (selectedAttribute === undefined && edgeData.status === 'CRITICAL') {
      setSelectedAttribute('edge-estimation');
    } else {
      setEdgeSizeBoundingBoxes([]);
    }
  }, [selectedAttribute, edgeEstimation.data]);

  return (
    <Box height='calc(100% - 5rem - 1px)' display='flex' flexDirection='column' gap={1} padding={2} overflow='scroll'>
      {/* Image Card */}
      <DetailImageQueryGuarded
        title={'delivery.loading_area'}
        imageQuery={image}
        downloadFileName={`${formatRecordingDownloadTimestamp(recording.observedAt)}.jpg`}
        boundingBoxes={edgeSizesBoundingBoxes}
      />

      {/* Prediction */}
      <WrapperCardWithTitle
        title={'recording.prediction'}
        adornment={<PredictionColoringTooltip />}
        sx={{ flex: '1 1 0', minHeight: '400px', maxHeight: '50vh' }}
      >
        <PredictionColumnsLayout
          materialTypeColumn={<MaterialColumnQueryGuarded materialClassificationQuery={materialClassification} />}
          attributesColumn={
            <AttributesColumnQueryGuarded
              attributeQueries={[soilClassification, volumeEstimation, edgeEstimation]}
              isMaterialClassificationPending={materialClassification.isPending}
              onAttributeSelect={handleAttributeSelect}
              selectedAttribute={selectedAttribute}
            />
          }
          billableMaterialColumn={<BillableMaterialColumn isUserAdmin={isUserAdmin} materials={resolvedMaterials} />}
        />
      </WrapperCardWithTitle>

      {/* Linked Delivery Note */}
      <WrapperCardWithTitle
        title={'recording.deliveryNote'}
        sx={{ flex: '1 1 0', minHeight: '300px', maxHeight: '50vh' }}
      >
        {recording.deliveryId ? (
          <DeliveryNoteGuarded delivery={delivery} />
        ) : (
          <DefaultErrorScreen defaultText={'error.deliveryNoteNotAvailable'} />
        )}
      </WrapperCardWithTitle>

      {recording.imageId ? <FeedbackSnackbar {...feedbackSnackbarManager} /> : null}
    </Box>
  );
};

function formatRecordingDownloadTimestamp(observedAt: Date): string {
  return DateTime.fromJSDate(observedAt).toLocal().toFormat('yyyy-LL-dd_HH-mm-ss');
}
